import '../operations.css';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { recipientRiskApi } from '../../api/reports';
import type { RecipientRiskSummary, RiskTier } from '../../api/reports';

const TIER_OPTIONS: { value: RiskTier | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'repeat_offender', label: 'Repeated exposure indicators' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

const TIER_LABELS: Record<RiskTier, string> = {
  repeat_offender: 'Repeated exposure',
  high: 'More indicators',
  medium: 'Some indicators',
  low: 'Fewer indicators',
};

const TIER_BADGE_CLASSES: Record<RiskTier, string> = {
  repeat_offender: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-green-100 text-green-800',
};

export function TierBadge({ tier }: { tier: RiskTier }) {
  return (
    <span
      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${TIER_BADGE_CLASSES[tier]}`}
    >
      {TIER_LABELS[tier]}
    </span>
  );
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatTime(iso: string): string {
  if (!iso) return 'N/A';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'N/A' : d.toLocaleString();
}

function recipientColumns() {
  return [
    {
      key: 'email',
      header: 'Recipient',
      render: (r: RecipientRiskSummary) => (
        <div>
          <span className="font-medium text-gray-900">{r.email}</span>
          {(r.first_name || r.last_name) && (
            <span className="block text-xs text-gray-500">
              {[r.first_name, r.last_name].filter(Boolean).join(' ')}
              {r.position ? ` — ${r.position}` : ''}
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'risk_tier',
      header: 'Observed pattern',
      render: (r: RecipientRiskSummary) => <TierBadge tier={r.risk_tier} />,
    },
    { key: 'total_sent', header: 'Sent' },
    { key: 'total_clicked', header: 'Clicked' },
    { key: 'total_submitted', header: 'Submitted' },
    { key: 'total_reported', header: 'Reported' },
    {
      key: 'click_rate',
      header: 'Click Rate',
      render: (r: RecipientRiskSummary) => formatPercent(r.click_rate),
    },
    {
      key: 'submit_rate',
      header: 'Submit Rate',
      render: (r: RecipientRiskSummary) => formatPercent(r.submit_rate),
    },
    {
      key: 'campaigns_clicked',
      header: 'Campaigns Clicked',
      render: (r: RecipientRiskSummary) => `${r.campaigns_clicked}/${r.total_campaigns}`,
    },
    {
      key: 'last_event_at',
      header: 'Last Activity',
      render: (r: RecipientRiskSummary) => formatTime(r.last_event_at),
    },
  ];
}

export function AtRiskRecipients() {
  const [tier, setTier] = useState<RiskTier | ''>('');
  const [selected, setSelected] = useState<RecipientRiskSummary | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['recipient-risk', tier],
    queryFn: async () => {
      const res = await recipientRiskApi.getSummaries(tier || undefined);
      return res.data;
    },
  });

  const { data: history, isLoading: historyLoading, isError: historyError, refetch: retryHistory } = useQuery({
    queryKey: ['recipient-history', selected?.email],
    queryFn: async () => {
      const res = await recipientRiskApi.getHistory(selected!.email);
      return res.data;
    },
    enabled: selected !== null,
  });

  return (
    <div className="operations-page space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="operations-eyebrow">Behavior intelligence</p>
          <h1 className="text-2xl font-bold text-gray-900">Observed behavior patterns</h1>
          <p className="text-gray-500 mt-1">
            Rates use each recipient's sent count as the denominator. These recorded campaign actions are not a prediction of intent or future behavior.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 mr-1">Tier:</span>
          {TIER_OPTIONS.map((opt) => (
            <Button
              key={opt.label}
              variant={tier === opt.value ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setTier(opt.value)}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {isError ? (
        <p role="alert" className="text-red-700">
          Could not load observed behavior patterns. Refresh the page to try again.
        </p>
      ) : isLoading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded"></div>
          ))}
        </div>
      ) : data ? (
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {tier ? `${TIER_LABELS[tier as RiskTier]} pattern` : 'Observed behavior patterns'}
          </h3>
          <Table
            columns={recipientColumns()}
            data={data}
            keyExtractor={(r) => r.email}
            onRowClick={(r) => setSelected(r)}
            emptyMessage="No recipients in this tier."
          />
        </Card>
      ) : null}

      <Modal
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `History — ${selected.email}` : 'History'}
        size="lg"
      >
        {historyLoading ? (
          <p className="text-gray-500">Loading history…</p>
        ) : historyError ? <div role="alert" className="space-y-4"><p className="text-red-700">Could not load recipient history. Try again.</p><Button variant="secondary" onClick={() => retryHistory()}>Try again</Button></div> : history && history.length > 0 ? (
          <ul className="recipient-event-list space-y-3">
            {history.map((entry, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-xs text-gray-400 w-40 shrink-0">
                  {formatTime(entry.time)}
                </span>
                <div>
                  <span className="text-sm font-medium text-gray-900">{entry.message}</span>
                  <span className="block text-xs text-gray-500">{entry.campaign_name}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No recorded events for this recipient.</p>
        )}
      </Modal>
    </div>
  );
}
